import Immutable from 'immutable';

export interface UsableAddresses {
  /** Addresses with a leading empty entry removed. */
  usableAddresses: Immutable.List<any>;
  /** Whether an empty first address was removed (display indices shift by -1). */
  firstAddressWasEmpty: boolean;
  /** Map a display index back to the original profile address index. */
  toOriginalIndex: (displayIndex: number) => number;
}

/**
 * Normalise a profile address list for display.
 *
 * A freshly created profile carries one empty address; this drops that leading
 * empty entry and returns a mapper from display index back to the original
 * profile index (indices shift by -1 when the empty entry is removed).
 * Centralised so the address book and both checkout selectors agree on indices.
 *
 * TODO: the backend should stop creating an empty placeholder address on
 * profile creation, and existing empty entries should be cleaned via an update
 * hook — then this normalisation (and the raw-vs-usable index handling in the
 * address book) can be removed. Tracked in Monday:
 * https://uncinc.monday.com/boards/1674548856/pulses/12320149073
 */
export function getUsableAddresses(
  addresses: Immutable.List<any>,
): UsableAddresses {
  let usableAddresses = Immutable.List(addresses);
  let firstAddressWasEmpty = false;

  if (usableAddresses.getIn([0, 'address_line1']) === null) {
    firstAddressWasEmpty = true;
    usableAddresses = usableAddresses.delete(0);
  }

  const toOriginalIndex = (i: number): number => (firstAddressWasEmpty ? i + 1 : i);

  return { usableAddresses, firstAddressWasEmpty, toOriginalIndex };
}

/**
 * The new value for a stored default-address index after the address at
 * `deletedIndex` is removed from the list:
 *   - null → the default itself was deleted (clear it),
 *   - current - 1 → the default sat after the deleted entry (shifts down one),
 *   - current → the default sat before it (unchanged).
 *
 * `current` is null when there is no default, in which case it stays null.
 */
export function defaultIndexAfterDelete(
  current: number | null,
  deletedIndex: number,
): number | null {
  if (current === null) return null;
  if (current === deletedIndex) return null;
  return current > deletedIndex ? current - 1 : current;
}
